import { useParams, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";

import {
  DetailTable,
  ExternalApprovalFlow,
  InvalidRoute,
  LoadingFrame,
} from "./ExternalApprovalFlow";
import {
  ComponentState,
  useComponentStateMachine,
} from "../../shared/hooks/useComponentStateMachine";
import { usePublicApiClient } from "../../hooks/usePublicApiClient";
import { ColorElement } from "../order-create/subcomponents/ProductDetailsLogo";
import { ErrorView } from "../../components/error-view/ErrorView";
import { DesignPrintItem } from "../order-create/subcomponents/DesignPrintItem";
import { Design } from "../../../core/models/entities/Design";
import {
  ApprovalType,
  shouldShowApprovalButtons,
} from "../../../core/models/entities/BaseLogo";
import { mapToDesign } from "../../../data/models/mappers/mapDesign";
import { StyleViewer } from "../design-create/components/style-viewer.component";
import { MultiDesignApprovalCarousel } from "./MultiDesignApprovalCarousel";
import { DesignImage } from "../../components/Design/DesignImage";

export function ExternalApprovalDesignScreen() {
  const [qp] = useSearchParams();
  const params = useParams();
  const { getDesign, placeVerdictDesign, getMultipleDesigns } =
    usePublicApiClient();
  const { dataTransition, state } = useComponentStateMachine();

  // Handle both single code and multiple keys scenarios
  const code = qp.get("code");
  const keys = qp.get("keys");
  const designId = params?.designId;

  // For multiple keys scenario
  if (keys) {
    const keyArray = keys.split(",");

    // Fetch multiple designs using the new API method
    const [designs, setDesigns] = useState<Design[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedDesignIndex, setSelectedDesignIndex] = useState(0);

    const normalizeDtos = (data: any) => (Array.isArray(data) ? data : [data]);

    const fetchDesignsWithFallback = async (keysParam: string[]) => {
      try {
        const data = await getMultipleDesigns(keysParam);
        return normalizeDtos(data);
      } catch {
        const results = await Promise.all(
          keysParam.map(async (k) => {
            try {
              const single = await getMultipleDesigns([k]);
              return normalizeDtos(single);
            } catch {
              return [];
            }
          })
        );
        const flattened = results.flat();
        if (flattened.length === 0) {
          throw new Error("No designs could be loaded");
        }
        return flattened;
      }
    };

    useEffect(() => {
      const load = async () => {
        try {
          const dtos = await fetchDesignsWithFallback(keyArray);
          const mappedDesigns = dtos.map(mapToDesign);
          setDesigns(mappedDesigns);
          setLoading(false);
        } catch (err) {
          console.error("Error fetching multiple designs:", err);
          setError("Failed to load designs");
          setLoading(false);
        }
      };
      load();
    }, [keys]);

    if (loading) return <LoadingFrame />;
    if (error) return <ErrorView />;
    if (designs.length === 0) return <div>No designs found</div>;

    const selectedDesign = designs[selectedDesignIndex];

    const handleDesignSelect = (design: Design, index: number) => {
      setSelectedDesignIndex(index);
    };

    const handleVerdict = async (approval: boolean, note?: string) => {
      // For multi-design approval, we need to handle verdict for the selected design
      const publicKey = selectedDesign.approval?.publicKey;
      if (!publicKey) {
        console.error("No public key found for design", selectedDesign.id);
        return;
      }

      try {
        await placeVerdictDesign(selectedDesign.id, approval, note, publicKey);
        const updatedDtos = await fetchDesignsWithFallback(keyArray);
        const mappedUpdatedDesigns = updatedDtos.map(mapToDesign);
        setDesigns(mappedUpdatedDesigns);
      } catch (error) {
        console.error(
          "Failed to place verdict for design",
          selectedDesign.id,
          ":",
          error
        );
        throw error; // Re-throw to let ExternalApprovalFlow handle the error
      }
    };

    // Check if all designs are completed using new approval logic
    const allDesignsCompleted = designs.every((design) => {
      // A design is completed if it doesn't need approval buttons (not pending for its type)
      return (
        !design.approvalType ||
        !design.approval ||
        !shouldShowApprovalButtons(design.approval, design.approvalType)
      );
    });

    return (
      <ExternalApprovalFlow
        subject="design"
        title={selectedDesign.title}
        isEditable={selectedDesign.isEditable}
        approvalType={selectedDesign.approvalType}
        approval={selectedDesign.approval}
        placeVerdict={handleVerdict}
        details={<DesignDetailTable design={selectedDesign} />}
        carousel={
          <MultiDesignApprovalCarousel
            designs={designs}
            onSelectDesign={handleDesignSelect}
            selectedIndex={selectedDesignIndex}
            approvalType={selectedDesign.approvalType}
          />
        }
        view={
          selectedDesign?.isEditable === false ||
          !(
            selectedDesign?.viewImages && selectedDesign.viewImages.length > 0
          ) ? (
            <DesignImage
              className="min-h-[325px] min-w-[450px] max-h-[700px] max-w-[1000px] object-scale-down bg-white rounded-bl"
              design={selectedDesign}
              useHighRes={true}
            />
          ) : (
            <StyleViewer views={selectedDesign.viewImages} />
          )
        }
        isMultiApproval={true}
        allItemsCompleted={allDesignsCompleted}
        onViewItemsAgain={() => {
          // Reset to show designs again - this callback ensures we stay in approval view
        }}
      />
    );
  }

  // Original single design logic
  if (!(code && designId)) return <InvalidRoute />;

  const { isError, isLoading, isSuccess, data } = getDesign(+designId, code);
  dataTransition(isLoading, isSuccess, isError);

  if (state === ComponentState.Failed) return <ErrorView />;
  if (state === ComponentState.Loading) return <LoadingFrame />;

  const design = mapToDesign(data!);

  const handleApproval = async (approval: boolean, note?: string) =>
    placeVerdictDesign(design.id, approval, note, code);

  // Let ExternalApprovalFlow handle approval button visibility using shouldShowApprovalButtons

  return (
    <ExternalApprovalFlow
      subject="design"
      title={design.title}
      isEditable={design.isEditable}
      approvalType={design.approvalType}
      approval={design.approval}
      placeVerdict={handleApproval}
      details={<DesignDetailTable design={design} />}
      view={
        design?.isEditable === false ||
        !(design?.viewImages && design.viewImages.length > 0) ? (
          <DesignImage
            className="min-h-[325px] min-w-[450px] max-h-[600px] max-w-[800px] object-scale-down bg-white rounded-bl"
            design={design}
            useHighRes={true}
          />
        ) : (
          <StyleViewer views={design.viewImages} />
        )
      }
    />
  );
}

interface DesignDetailTableProps {
  design: Design;
}
export const DesignDetailTable = ({ design }: DesignDetailTableProps) => (
  <div className="flex flex-col flex-1">
    <div className="font-bold mb-6">Details</div>

    <DetailTable
      fields={{
        ...(design.productMul && {
          "Item No": design.productMul,
        }),
        Name: design.title,
        Manufacturer:
          design.isEditable === false
            ? design.navManufacturerName ?? "N/A"
            : design.style?.manufacturer?.name ??
              design.navManufacturerName ??
              "N/A",
        Model:
          design.isEditable === false
            ? design.navStyleName ?? "N/A"
            : design.style?.modelAndNameWithoutHyphen ??
              design.style?.name ??
              design.navStyleName ??
              "N/A",
      }}
    />

    <div className="relative flex flex-col flex-1 w-full mb-4">
      <div className="absolute overflow-y-scroll h-full w-full">
        <div className="flex flex-col gap-y-4">
          {design.designLogos.map((designLogo, i) => {
            const views = design.style?.styleViews ?? [];
            const relevantViews = views.filter(
              (v) => v.id == designLogo.viewId
            );
            const placements = relevantViews.map((x) => x.view).join("");

            return (
              <DesignPrintItem
                key={i}
                logo={designLogo.logo}
                placements={placements}
                colors={
                  <ColorElement
                    logo={designLogo.logo}
                    colors={designLogo.logo.colorDetails}
                  />
                }
              />
            );
          })}
        </div>
      </div>
    </div>
  </div>
);
